import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, Tag, Input} from 'antd';
import type {TableColumnsType} from 'antd';
import {ReadOutlined, RollbackOutlined} from '@ant-design/icons';
import {useGetUserDetails} from 'hooks/users/useGetUsers';
import {useGetOrdersByUser} from 'hooks/order/useGetOrder';
import type {SearchProps} from 'antd/es/input/Search';

import {TableCustom} from 'components/element/TableCustom';
import {PaginationCustom} from 'components/element/PaginationCustom';
import {addKeyData, formatNumberValue, formatDate} from 'utils';
import {
  ORDER_STATUS,
  ORDER_STATUS_COLOR,
  ORDER_STATUS_LABEL,
} from 'types/order';

export const OrderUser: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [filters, setFilters] = useState({});
  const {data: getData, isLoading} = useGetOrdersByUser({
    ...filters,
    userID: params?.id,
  });
  const {data: client, isLoading: isLoadingClient} = useGetUserDetails(
    params?.id || '',
  );
  const columns: TableColumnsType = [
    {
      title: 'Mã Order',
      dataIndex: 'code',
      key: 'code',
      align: 'center',
      fixed: 'left',
      width: 130,
      render: data => <span className="whitespace-nowrap font-medium">#{data}</span>,
    },
    {
      title: 'Địa chỉ người nhận',
      dataIndex: 'addressReceiver',
      key: 'addressReceiver',
      align: 'center',
      width: 250,
      render: data => (
        <div className="max-w-[240px] truncate" title={data}>
          {data}
        </div>
      ),
    },
    {
      title: 'Tên Shop',
      dataIndex: 'shop',
      key: 'shop',
      align: 'center',
      width: 180,
      render: (data: any) => {
        return (
          <div className="max-w-[170px] truncate font-medium" title={data?.name}>
            {data?.name}
          </div>
        );
      },
    },
    {
      title: 'Tên người nhận',
      dataIndex: 'client',
      key: 'client',
      align: 'center',
      width: 160,
      render: (data: any) => {
        return <div className="whitespace-nowrap">{data?.name}</div>;
      },
    },
    {
      title: 'Phụ phí',
      dataIndex: 'subFee',
      key: 'subFee',
      align: 'center',
      width: 130,
      render: data => {
        return (
          <div className="whitespace-nowrap">
            <p>{formatNumberValue(data)}</p>
          </div>
        );
      },
    },
    {
      title: 'Phí Ship',
      dataIndex: 'shipFee',
      key: 'shipFee',
      align: 'center',
      width: 130,
      render: data => {
        return (
          <div className="whitespace-nowrap">
            <p>{formatNumberValue(data)}</p>
          </div>
        );
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 170,
      render: (data: string) => {
        return (
          <Tag color={ORDER_STATUS_COLOR[data] || 'default'}>
            {ORDER_STATUS_LABEL[data as ORDER_STATUS] || data.toLocaleUpperCase()}
          </Tag>
        );
      },
      filters: [
        {value: ORDER_STATUS.Initialize, text: ORDER_STATUS_LABEL[ORDER_STATUS.Initialize]},
        {value: ORDER_STATUS.Waiting, text: ORDER_STATUS_LABEL[ORDER_STATUS.Waiting]},
        {value: ORDER_STATUS.DriverVerify, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverVerify]},
        {value: ORDER_STATUS.DriverConfirmed, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverConfirmed]},
        {value: ORDER_STATUS.DriverToShop, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverToShop]},
        {value: ORDER_STATUS.ClientPaying, text: ORDER_STATUS_LABEL[ORDER_STATUS.ClientPaying]},
        {value: ORDER_STATUS.DriverConfirmingPayment, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverConfirmingPayment]},
        {value: ORDER_STATUS.PaymentFailure, text: ORDER_STATUS_LABEL[ORDER_STATUS.PaymentFailure]},
        {value: ORDER_STATUS.DriverDelivery, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverDelivery]},
        {value: ORDER_STATUS.Completed, text: ORDER_STATUS_LABEL[ORDER_STATUS.Completed]},
        {value: ORDER_STATUS.AutoCompleted, text: ORDER_STATUS_LABEL[ORDER_STATUS.AutoCompleted]},
        {value: ORDER_STATUS.Canceled, text: ORDER_STATUS_LABEL[ORDER_STATUS.Canceled]},
      ],
    },
    {
      title: 'Ngày đặt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      width: 160,
      render: data => {
        return (
          <div className="whitespace-nowrap">
            <p>{formatDate(data)}</p>
          </div>
        );
      },
    },
    {
      title: 'Thao tác',
      dataIndex: '',
      key: 'action',
      align: 'center',
      width: 170,
      render: (record: any) => {
        return (
          <div className="flex justify-center gap-2 px-0">
            <Button
              onClick={() => {
                navigate(`/orders/${record?._id}`, {
                  state: {from: `/orders`},
                });
              }}
              icon={<ReadOutlined />}
              className="!border-white bg-[#eca52b] hover:!bg-[#edb555] !text-white !font-medium">
              Chi tiết đơn hàng
            </Button>
          </div>
        );
      },
    },
  ];
  const handleTableChange = (filter: any, sorter: any) => {
    const {status} = filter;
    if (status) {
      setFilters({...filters, status: status});
    } else {
      setFilters({...filters, status: []});
    }
  };
  const onSeachValue: SearchProps['onSearch'] = (value, _e, info) => {
    setFilters({...filters, code: value});
  };
  // useEffect(() => {
  //   setFilters();
  // }, [params?.id]);
  return (
    <div className="container mx-auto">
      <div className="flex-col gap-4">
        <div className="flex justify-between items-center">
          <div style={{padding: '20px 0px'}}>
            <h2 className="text-2xl font-bold" style={{margin: 0}}>
              Lịch sử mua hàng của: {client?.name}
            </h2>
            <p style={{padding: '6px 0'}}>
              {' '}
              Tống số lượng đơn hoàn thành: {getData?.countCompleted ??
                0} / {getData?.totalCount ?? 0}{' '}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              icon={<RollbackOutlined />}
              onClick={() => {
                navigate(-1);
              }}>
              Trở về
            </Button>
            <Input.Search
              className="w-96"
              placeholder="Tìm kiếm mã đơn hàng"
              onSearch={onSeachValue}
              enterButton
            />
          </div>
        </div>
        <div className=" flex flex-col items-end gap-3">
          <TableCustom
            isLoading={isLoading}
            columns={columns}
            data={addKeyData(getData?.result)}
            onChange={handleTableChange}
            scroll={{x: 1550, y: 590}}
          />
          <PaginationCustom
            totalPage={getData?.totalPage ?? 1}
            current={getData?.page ?? 1}
            pageSize={getData?.pageSize ?? 10}
            onChange={(page, pageSize) => {
              console.log(page, pageSize);
              setFilters({...filters, page, pageSize});
            }}
            onShowSizeChange={(current, size) => {
              setFilters({...filters, page: current, pageSize: size});
            }}
          />
        </div>
      </div>
    </div>
  );
};
